import { useNavigate } from '@tanstack/react-router';
import { Button } from '@workspace/ui/components/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@workspace/ui/components/card';
import { Spinner } from '@workspace/ui/components/spinner';
import { Home, Loader2, Shield, ShieldCheck } from 'lucide-react';
import { useCallback } from 'react';
import { m } from '~/paraglide/messages.js';
import { useKYCStatus, useStartKYC } from '../-hooks/use-kyc';
import { KYCIframe } from './kyc-iframe';

export function KYCVerification() {
  const { data, isLoading } = useKYCStatus();
  const startKYCMutation = useStartKYC();
  const navigate = useNavigate();

  const handleStartVerification = useCallback(() => {
    startKYCMutation.mutate();
  }, [startKYCMutation]);

  const handleGoHome = useCallback(() => {
    navigate({ to: '/' });
  }, [navigate]);

  if (isLoading) {
    return <Spinner message={m['verify_identity.loading']()} />;
  }

  if (!data) {
    return (
      <div className="flex items-center justify-center min-h-full">
        <div className="w-full max-w-md mx-auto">
          <Card>
            <CardHeader className="text-center">
              <Shield className="h-12 w-12 mx-auto text-blue-600 mb-2" />
              <CardTitle>{m['verify_identity.title']()}</CardTitle>
            </CardHeader>
            <CardContent className="text-center space-y-4">
              <p className="text-muted-foreground">
                {m['verify_identity.description']()}
              </p>
              <div className="bg-muted p-4 rounded-lg text-left">
                <h4 className="font-medium mb-2">
                  {m['verify_identity.requirements_title']()}
                </h4>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• {m['verify_identity.requirement_id']()}</li>
                  <li>• {m['verify_identity.requirement_personal_info']()}</li>
                </ul>
              </div>
              <Button
                onClick={handleStartVerification}
                disabled={startKYCMutation.isPending}
                className="w-full"
                size="lg"
              >
                {startKYCMutation.isPending ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Shield className="h-4 w-4 mr-2" />
                )}
                {m['verify_identity.start_verification']()}
              </Button>
              {startKYCMutation.error && (
                <p className="text-sm text-red-600">
                  {m['verify_identity.error_prefix']()}{' '}
                  {startKYCMutation.error.message ||
                    m['verify_identity.error_default']()}
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (data.status === 'approved') {
    return (
      <div className="flex items-center justify-center min-h-full">
        <Card className="w-full max-w-md mx-auto border-green-200">
          <CardHeader className="text-center">
            <ShieldCheck className="h-12 w-12 mx-auto text-green-600 mb-2" />
            <CardTitle className="text-green-700">
              {m['verify_identity.approved_title']()}
            </CardTitle>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <p className="text-muted-foreground">
              {m['verify_identity.approved_description']()}
            </p>
            <Button onClick={handleGoHome} className="w-full" size="lg">
              <Home className="h-4 w-4 mr-2" />
              {m['verify_identity.go_home']()}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return <KYCIframe kycData={data} />;
}
